import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import type { BranchManagementRepository } from "../domain";
import {
  archivedBranchFromDto,
  archivedBranchMatchFromDto,
  branchCreateToDto,
  branchFromDto,
  branchUpdateToDto,
} from "./organizationMappers";
import {
  archivedBranchListResponseDtoSchema,
  archivedBranchMatchResponseDtoSchema,
  branchCreateWriteDtoSchema,
  branchListResponseDtoSchema,
  branchResponseDtoSchema,
  branchUpdateWriteDtoSchema,
  messageResponseDtoSchema,
} from "./organizationDtos";

const BRANCHES_PATH = `${API_PREFIX}/branches`;

export function createBranchManagementRepository(
  http: HttpClient,
): BranchManagementRepository {
  return {
    async list(query) {
      const response = await http.get(BRANCHES_PATH, { params: query });
      return paginatedFromDto(
        branchListResponseDtoSchema.parse(response),
        branchFromDto,
      );
    },

    async getById(id) {
      const response = await http.get(`${BRANCHES_PATH}/${id}`);
      return branchFromDto(branchResponseDtoSchema.parse(response).data);
    },

    async create(input) {
      const dto = branchCreateWriteDtoSchema.parse(branchCreateToDto(input));
      try {
        const response = await http.post(BRANCHES_PATH, dto);
        const created = branchResponseDtoSchema.parse(response);
        const branch = branchFromDto(created.data);
        return {
          status: "created",
          branch,
          ...(created.message !== undefined
            ? { responseMessage: created.message }
            : {}),
        };
      } catch (error) {
        // Same archived-match-as-409 shape every other create endpoint in
        // this app uses: nothing was created, the body just offers a
        // restore. Disambiguated by shape (the trimmed match under `data`),
        // not the `code` string.
        const archived =
          error instanceof HttpError && error.status === 409
            ? archivedBranchMatchResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (archived === undefined || !archived.success) throw error;
        return {
          status: "archived-match",
          match: archivedBranchMatchFromDto(archived.data.data),
          message: archived.data.message,
        };
      }
    },

    async update(id, input) {
      const dto = branchUpdateWriteDtoSchema.parse(branchUpdateToDto(input));
      const response = await http.patch(`${BRANCHES_PATH}/${id}`, dto);
      return branchFromDto(branchResponseDtoSchema.parse(response).data);
    },

    async archive(id) {
      const response = await http.delete(`${BRANCHES_PATH}/${id}`);
      messageResponseDtoSchema.parse(response);
    },

    async listArchived(query) {
      const response = await http.get(`${BRANCHES_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedBranchListResponseDtoSchema.parse(response),
        archivedBranchFromDto,
      );
    },

    async restore(id) {
      const response = await http.patch(`${BRANCHES_PATH}/${id}/restore`);
      const parsed = branchResponseDtoSchema.parse(response);
      const branch = branchFromDto(parsed.data);
      return {
        ...branch,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
  };
}
